import type { AssistantUsage, ChatEntry } from './useAssistant'

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

// Format a USD cost estimate, rounded to cents.
export const formatUsd = (n: number): string => usdFormatter.format(n)

// Escalate a cost's emphasis with its size: bold over $1, bold + red over $5.
// Returns only weight/color classes (no base color), so it can layer over whatever
// text color the surrounding element already sets.
export const costEmphasisClass = (cost: number | undefined): string =>
  cost === undefined ? '' : cost > 5 ? 'font-bold text-red-600' : cost > 1 ? 'font-bold' : ''

// Sum the per-turn usage (token buckets + cost estimate) across every turn that
// recorded it, into one conversation-level total. Mirrors the server-side rollup so
// the numbers match; kept on the client so it updates live while a turn streams in.
export function sumTurnUsage(entries: ChatEntry[]): AssistantUsage | undefined {
  let input = 0
  let output = 0
  let cacheRead = 0
  let cacheCreate = 0
  let cost: number | undefined
  let any = false
  for (const entry of entries) {
    if (entry.kind !== 'turn' || !entry.usage) continue
    any = true
    const u = entry.usage
    input += u.inputTokens
    output += u.outputTokens
    cacheRead += u.cacheReadInputTokens ?? 0
    cacheCreate += u.cacheCreationInputTokens ?? 0
    if (u.costUsd !== undefined) cost = (cost ?? 0) + u.costUsd
  }
  if (!any) return undefined
  return {
    inputTokens: input,
    outputTokens: output,
    ...(cacheRead > 0 && { cacheReadInputTokens: cacheRead }),
    ...(cacheCreate > 0 && { cacheCreationInputTokens: cacheCreate }),
    ...(cost !== undefined && { costUsd: Math.round(cost * 1e6) / 1e6 }),
  }
}
